import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { useConfig } from '../../config';
import { useFormatting, useTranslation } from '../../i18n/i18n';
import { useApi, useCacheInvalidator, useCms } from '../../state/api';
import { Res } from '../../state/api-resources';
import { useSession } from '../../state/session';
import { ApiBonusActivate } from '../../types';
import { AccountPageNavigation } from '../AccountPageNavigation/AccountPageNavigation';
import { AlertMessages, useAlerts } from '../AlertMessages/AlertMessages';
import { CheckmarkList } from '../CheckmarkList/CheckmarkList';
import { CmsComponent } from '../cms/CmsComponent';
import { ContentCard } from '../ContentCard/ContentCard';
import { Button } from '../Form/Button';
import { Icon, coins, coins_two, gift } from '../Icon/Icon';
import { Main } from '../Main/Main';
import { renderTemplate } from '../utils';
import { BonusBox } from './BonusBox/BonusBox';
import { BonusTable } from './BonusTable';
import { BonusWallet } from './BonusWallet/BonusWallet';

export const BonusLoggedIn = () => {
	const t = useTranslation().bonusPage;
	const generic = useTranslation().generic;
	const alerts = useAlerts();
	const { api } = useConfig();
	const { asPrice } = useFormatting();
	const { user } = useSession();
	const mutate = useCacheInvalidator();

	const { data: bonusBalance } = useApi(Res.bonusBalance, {});
	const { data: bonusRegisterData, isLoading: isLoadingBonusRegisterData } = useApi(Res.bonusRegisterData, {});
	const { data: description, isLoading: isLoadingDescription } = useCms(Res.cmsComponent, {
		slug: 'bonus-description',
	});
	const { data: pitch, isLoading: isLoadingPitch } = useCms(Res.cmsComponent, { slug: 'bonus-pitch' });

	const history = useHistory();
	const urls = useTranslation().routes;

	async function joinBonusProgramme() {
		const payload = [{ op: 'replace', path: '/bonus', value: 'true' }];

		alerts.clear();

		try {
			await axios.patch(`${api}me/medium.json`, payload, {
				withCredentials: true,
			});
		} catch (error) {
			// the page that follows congratulates them on joining, so it must not be reached
			// unless they have
			alerts.set({ type: 'error', message: generic.somethingWentWrong });
			return;
		}

		mutate(Res.session, {});
		history.push(`${urls.bonus}?activated=true`);
	}

	async function handleActivateBonus() {
		alerts.clear();

		try {
			await axios.post<ApiBonusActivate>(
				api + Res.bonusActivate.uri({}),
				{
					c: bonusRegisterData!.bonusCode,
					orderId: bonusRegisterData!.order.id,
				},
				{ withCredentials: true }
			);
		} catch (error) {
			alerts.set({ type: 'error', message: generic.somethingWentWrong });
			return;
		}

		mutate(Res.session, {});
		history.push(`${urls.bonus}?activated=true`);
	}

	if (user?.bonus) {
		const coinIcon = <Icon shape={coins} size={120} />;
		const twoCoinsIcon = <Icon shape={coins_two} size={120} />;
		return (
			<Main>
				<AccountPageNavigation />
				<AlertMessages alerts={alerts} />
				<div id="bonus-page">
					<h1 className="private-page-heading">{t.bonus}</h1>
					<div className="bonus-info-cards-grid">
						<BonusWallet />
						<BonusBox
							icon={twoCoinsIcon}
							heading={t.notYetActivated}
							bonusValue={bonusBalance?.notActivatedBonusAmount}
						/>
						<BonusBox icon={coinIcon} heading={t.totalAmountPayed} bonusValue={bonusBalance?.usedBonus} />
					</div>
					<BonusTable />

					{!isLoadingDescription && (
						<ContentCard>
							<CmsComponent structure={description} />
						</ContentCard>
					)}
				</div>
			</Main>
		);
	}

	return (
		<Main>
			<AccountPageNavigation />
			<AlertMessages alerts={alerts} />
			<div id="bonus-page">
				<h1 className="big">{t.bonus}</h1>
				<div className="bonus-register-cards-grid">
					<ContentCard className="register">
						<div style={{ maxWidth: '40rem' }}>
							<h2>{t.registerAsBonusCustomer}</h2>
							<p>{t.registerAsBonusCustomerCopy}</p>
							<CheckmarkList className="black compact">
								<li>{t.registerChecklistA}</li>
								<li>{t.registerChecklistB}</li>
								<li>{t.registerChecklistC}</li>
							</CheckmarkList>
						</div>
					</ContentCard>
					{!isLoadingBonusRegisterData && (
						<div className="bonus-box">
							<ContentCard className="bonus-wallet">
								<Icon shape={gift} size={100} />
								{bonusRegisterData?.bonusAmount ? (
									<h2>
										{renderTemplate(t.cashInOnPreviousPurchase, {
											// a balance, not a price, so its two decimals stay
											amount: asPrice(bonusRegisterData.bonusAmount, 2, false),
										})}
									</h2>
								) : (
									<h2>{t.becomeBonusCustomerToday}</h2>
								)}
							</ContentCard>
							<Button
								className=""
								onClick={bonusRegisterData?.bonusCode ? handleActivateBonus : joinBonusProgramme}
							>
								{t.becomeBonusCustomer}
							</Button>
						</div>
					)}
				</div>
				{!isLoadingPitch && (
					<ContentCard>
						<CmsComponent structure={pitch} />
					</ContentCard>
				)}
				{!isLoadingDescription && (
					<ContentCard>
						<CmsComponent structure={description} />
						<div className="text-center">
							<Button
								className="big"
								onClick={bonusRegisterData?.bonusCode ? handleActivateBonus : joinBonusProgramme}
							>
								{t.becomeBonusCustomer}
							</Button>
						</div>
					</ContentCard>
				)}
			</div>
		</Main>
	);
};
